import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { fmtTime, durationFromNow } from '../lib/format';
import { useAuth } from '../stores/auth.store';
import { DutyLogDetailDialog } from '../components/DutyLogDetailDialog';

interface ActiveDuty {
  id: string;
  locationName: string;
  startAt: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  dutyType: { name: string };
  officer?: {
    firstName: string;
    lastName: string;
    unit?: { name: string } | null;
  };
}

interface DashboardData {
  inProgressNow: number;
  completedToday: number;
  totalLogsToday: number;
  pendingOfficers: number;
  totalHours: number;
  activeNow: ActiveDuty[];
}

export function SupervisorDashboardPage() {
  const officer = useAuth((s) => s.officer);
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [, setTick] = useState(0);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<DashboardData>('/app/sv/dashboard/today');
      setData(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // อัพเดทดูแลให้ duration อัพเดท
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (officer && officer.role === 'officer') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50 pb-24">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-500">
          ‹
        </button>
        <div>
          <h1 className="text-base font-semibold">สรุปวันนี้</h1>
          <p className="text-xs text-gray-500">
            {new Date().toLocaleDateString('th-TH', { dateStyle: 'long' })}
          </p>
        </div>
        <button
          onClick={load}
          className="ml-auto rounded-full px-2 text-gray-500 active:bg-gray-100"
          aria-label="รีโหลด"
        >
          ↻
        </button>
      </header>

      <main className="space-y-4 p-4">
        {loading || !data ? (
          <div className="card p-6 text-center text-sm text-gray-500">กำลังโหลด...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <StatCard
                label="กำลังออกตรวจ"
                value={data.inProgressNow}
                accent="text-amber-600"
              />
              <StatCard
                label="จบงานวันนี้"
                value={data.completedToday}
                accent="text-emerald-600"
              />
              <StatCard
                label="ชั่วโมงรวมวันนี้"
                value={data.totalHours.toFixed(2)}
                suffix=" ชม."
                accent="text-line"
              />
              <StatCard
                label="รออนุมัติ"
                value={data.pendingOfficers}
                accent="text-red-600"
                onClick={() => navigate('/supervisor/officers')}
              />
            </div>

            <section>
              <h2 className="mb-2 text-sm font-semibold">เจ้าหน้าที่ที่กำลังออกตรวจ ({data.activeNow.length})</h2>
              {data.activeNow.length === 0 ? (
                <div className="card p-6 text-center text-sm text-gray-500">
                  ไม่มีเจ้าหน้าที่กำลังออกตรวจ
                </div>
              ) : (
                <div className="space-y-2">
                  {data.activeNow.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setSelectedId(l.id)}
                      className="card flex w-full items-start gap-3 text-left active:bg-gray-50"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        🚓
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">
                          {l.officer
                            ? `${l.officer.firstName} ${l.officer.lastName}`
                            : '-'}
                          {l.officer?.unit && (
                            <span className="ml-2 text-xs text-gray-500">
                              ({l.officer.unit.name})
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-line">{l.locationName}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {l.dutyType.name} • เริ่ม {fmtTime(l.startAt)} • ผ่านไป{' '}
                          {durationFromNow(l.startAt)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <DutyLogDetailDialog
        dutyLogId={selectedId}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  suffix,
  onClick,
}: {
  label: string;
  value: number | string;
  accent: string;
  suffix?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent}`}>
        {value}
        {suffix && <span className="ml-1 text-sm font-medium">{suffix}</span>}
      </p>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="card text-left active:bg-gray-50">
        {content}
      </button>
    );
  }
  return <div className="card">{content}</div>;
}
